import { create } from 'zustand';

/**
 * Minimum custodial USDC agent setup needs. Mirrors
 * NEW_HYPERCORE_ACCOUNT_BOOTSTRAP_RAW (2 USDC) in MoonHub.API's agent setup: the
 * bootstrap moves USDC out of the Snappy Wallet onto HyperCore, so below this the
 * setup cannot complete no matter how often it is retried.
 */
export const HYPERCORE_BOOTSTRAP_MINIMUM_USDC = 2;

/**
 * Backend code for "agent setup cannot run, the Snappy Wallet holds less than
 * {@link HYPERCORE_BOOTSTRAP_MINIMUM_USDC}". It arrives two ways: as the `code` of
 * a rejected `POST /speed/agent/reactivate` (newer backends fail fast, currently
 * HTTP 422 — match on the code, never on the status) and as the `code` of a
 * `failed` agentSetup job (older ones only find out inside the job, minutes after
 * the POST already answered `queued`).
 */
export const HIP3_SETUP_FUNDING_REQUIRED_CODE = 'HIP3_SETUP_FUNDING_REQUIRED';

export interface ActivationFundingInput {
    /** `GET /speed/agent/status`: null until an agent has been approved at least once. */
    agentAddress: string | null;
    /** `GET /speed/agent/status`: null until an agent has been approved at least once. */
    validUntil: number | null;
    /**
     * `GET /speed/agent/status`. Only a positive signal: older backends leave it false
     * for a wallet that has never run setup at all, which is exactly the case that
     * cannot succeed unfunded.
     */
    bootstrapRequired: boolean;
    /**
     * True once `GET /speed/agent/status` has answered. An unread status looks exactly
     * like a never-activated one, so nothing is decided before it lands.
     */
    agentStatusKnown: boolean;
    /** Custodial USDC; null while unknown (loading, or an errored read). */
    numericBalance: number | null;
    isBalanceLoading: boolean;
    /**
     * The backend already refused, or failed, a setup for funding, or a setup
     * job parked with nothing the client can do. Sticky, because the status read alone
     * would say the activation is fine to retry.
     */
    fundingBlockedByBackend: boolean;
}

/**
 * Whether activating the Snappy Wallet needs a deposit FIRST, i.e. whether offering
 * "Reactivate" would be offering a button that cannot succeed.
 *
 * Two conditions make the HyperCore bootstrap part of the run: the backend says so
 * (`bootstrapRequired`), or the agent has never been approved at all (a brand-new
 * wallet, where `bootstrapRequired` is still false because the flag is only raised
 * after a first setup's adapter phase ran). Either way the bootstrap deposits USDC
 * out of the custodial wallet, so below the minimum the job dies with
 * {@link HIP3_SETUP_FUNDING_REQUIRED_CODE}.
 *
 * An account that HAS an approved agent re-approves fine on a zero balance (no funds
 * move), so it must never be told to deposit.
 */
export function isActivationFundingRequired(
    input: ActivationFundingInput,
): boolean {
    const knownBalance =
        !input.isBalanceLoading && input.numericBalance !== null
            ? input.numericBalance
            : null;
    // enough funds is proof the block is stale, whatever the backend said earlier
    const isFunded =
        knownBalance !== null &&
        knownBalance >= HYPERCORE_BOOTSTRAP_MINIMUM_USDC;

    if (input.fundingBlockedByBackend) {
        return !isFunded;
    }

    if (!input.agentStatusKnown) {
        return false;
    }

    const neverActivated =
        input.agentAddress === null && input.validUntil === null;
    if (!input.bootstrapRequired && !neverActivated) {
        return false;
    }

    // an unknown balance is not a shortfall: never push a user to deposit on a
    // reading we do not have.
    if (knownBalance === null) {
        return false;
    }
    return knownBalance < HYPERCORE_BOOTSTRAP_MINIMUM_USDC;
}

interface ActivationFundingBlockState {
    /** True once the backend refused or failed an activation for want of funds. */
    isBlocked: boolean;
    markBlocked: () => void;
    clear: () => void;
}

/**
 * Shared, so the surface that fires the reactivate POST is not the only one that
 * learns it was refused. `useAgentReactivation` has ~8 live instances and the banner
 * reads its funding state through `useSnappyWalletState`, i.e. through a DIFFERENT
 * instance than the one whose mutation errored.
 */
export const useActivationFundingBlock = create<ActivationFundingBlockState>(
    (set) => ({
        isBlocked: false,
        markBlocked: () => set({ isBlocked: true }),
        clear: () => set({ isBlocked: false }),
    }),
);
